import React from "react";
import { createUseStyles } from "react-jss";
import { useNavigate, useLocation } from "react-router-dom";
import { EuiEmptyPrompt, EuiProgress, EuiText, EuiSpacer, EuiIcon } from "@elastic/eui";
import Button from "components/Buttons/Button";
import AddButton from "components/Buttons/AddButton";
import { NoDataIcon } from "components/Icons";
import {
  REDIRECT_TO_SILENCING_WINDOWS,
  REDIRECT_TO_INTEGRATIONS,
  REDIRECT_TO_DATA_COLLECTORS,
} from "settings/menu/admin_console";
import { noDataUrl, COLORS } from "Consts";
import NoWaitTime from "./NoWaitTime";

const useStyles = createUseStyles({
  wrapper: {
    alignContent: "center",
    display: "flex",
    justifyContent: "center",
    paddingTop: "20px",
  },
  title: {
    color: "#69707D",
    "text-align": "center",
    "font-family": "Inter",
    "font-size": "var(--fs16)",
    "font-style": "normal",
    "font-weight": "600",
    "line-height": "normal",
  },
  text: {
    fontSize: "var(--fs12)",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
    textAlign: "left",
  },
  see_details: {
    color: "var(--cyan)",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

type NoDataProps = {
  height?: string | number;
  loading?: boolean;
  noData: number;
  type?: "card" | "table" | "page" | "chart" | "waitTime";
  onClick?: any;
};

export const getNoDataMessage = (isLoading: boolean) => {
  return isLoading ? (
    "Loading..."
  ) : (
    <h2>
      <div
        style={{
          display: "inline-flex",
          paddingRight: "8px",
          position: "relative",
          top: "4px",
        }}>
        <NoDataIcon />
      </div>

      <div style={{ display: "inline-flex" }}>No data available</div>
    </h2>
  );
};

const NoData: React.FC<NoDataProps> = React.memo(
  ({ loading = false, noData = 0, height, type = "card", onClick = () => {} }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();

    const exceptions = [
      "/admin_console/monitoring/silencing_windows",
      "/admin_console/monitoring/alert_rules",
      "/admin_console/monitoring/notification_channels",
      "/admin_console/monitoring/custom_monitoring_queries",
    ];

    const getTitle = () => {
      if (type === "card") return "No data to display";
      if (type === "page") return "No Data to Display on Page";
      if (type === "table" || type === "chart") return "No data available";
    };

    const getContent = () => {
      if (type === "waitTime") {
        return <NoWaitTime />;
      }

      if (type === "page") {
        return (
          <div>
            <EuiText className={classes.text}>
              There may be several reasons why there is no data. Here are some suggestions:
            </EuiText>
            <EuiText className={classes.text}>
              <ul style={{ paddingLeft: "16px", textAlign: "left" }}>
                <li>If servers were just added, wait a few minutes to allow connection.</li>
                <li>Select a different time frame and/or filters.</li>
                <li>
                  Check data collection errors,{" "}
                  <span
                    className={classes.see_details}
                    onClick={() => {
                      navigate(REDIRECT_TO_DATA_COLLECTORS);
                    }}>
                    see details
                  </span>
                  .
                </li>
              </ul>
            </EuiText>
            <EuiSpacer size="xs" />
            <Button
              type="empty"
              text="Learn More"
              onClick={() => {
                window.open(noDataUrl, "_blank");
              }}
            />
          </div>
        );
      }

      if (type === "card") {
        return (
          <div>
            <EuiText className={classes.text}>No data found for the selected filters.</EuiText>
            <EuiSpacer size="xs" />
            <Button
              type="empty"
              text="Learn More"
              onClick={() => {
                window.open(noDataUrl, "_blank");
              }}
            />
          </div>
        );
      }

      return (
        <div>
          <EuiText className={classes.text}>No data found for the selected filters.</EuiText>
          <EuiText className={classes.text}>
            <ul style={{ paddingLeft: "16px" }}>
              <li>Try a different time frame or adjust your filters.</li>
              <li>Review any potential data collection issues.</li>
            </ul>
          </EuiText>
          <EuiSpacer size="xs" />
          <Button
            type="empty"
            text="Learn More"
            onClick={() => {
              window.open(noDataUrl, "_blank");
            }}
          />
        </div>
      );
    };

    return (
      <>
        {loading && (
          <EuiProgress
            color={COLORS.$progressBlue}
            size="xs"
            style={{ backgroundColor: "#f0f2f5", color: COLORS.$progressBlue }}
          />
        )}

        {!loading && noData === 0 && !exceptions.includes(location?.pathname) && (
          <>
            <EuiEmptyPrompt
              body={getContent()}
              className={classes.wrapper}
              hasBorder={false}
              style={{
                height: type === "card" ? "auto" : height,
                padding: type === "card" ? "0px" : "20px",
              }}
              title={
                type !== "waitTime" ? (
                  <h2 className={classes.title}>
                    <div
                      style={{
                        display: "inline-flex",
                        paddingRight: "8px",
                        position: "relative",
                        top: "4px",
                      }}>
                      <NoDataIcon />
                    </div>

                    <div style={{ display: "inline-flex" }}>{getTitle()}</div>
                  </h2>
                ) : undefined
              }
              titleSize="s"
            />
          </>
        )}

        {!loading &&
          (noData === 9999 || (location?.pathname === exceptions[0] && noData === 0)) && (
            <>
              <EuiEmptyPrompt
                body={
                  <div>
                    <EuiText className={classes.text}>
                      <div style={{ paddingLeft: "16px", textAlign: "center" }}>
                        <span>To Schedule quiet periods for maintenance or deployments.</span>
                      </div>
                    </EuiText>
                    <EuiSpacer size="m" />
                    {/* <AddButton
                      text="Silencing Window"
                      onClick={() => {
                        if (noData === 0) onClick();
                        if (noData === 9999) navigate(REDIRECT_TO_SILENCING_WINDOWS);
                      }}
                    />
                    <EuiSpacer size="s" /> */}
                  </div>
                }
                icon={
                  <div
                    style={{
                      borderRadius: "50%",
                      backgroundColor: "#1b8dcd22",
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                    <EuiIcon type="clock" color="var(--cyan)" size="l" />
                  </div>
                }
                className={classes.wrapper}
                hasBorder={false}
                style={{
                  height,
                  padding: 0,
                }}
                title={
                  <h2 className={classes.title}>
                    <div style={{ display: "inline-flex" }}>Pause alerts during maintenance.</div>
                  </h2>
                }
                titleSize="s"
              />
            </>
          )}

        {!loading && location?.pathname === exceptions[1] && noData === 0 && (
          <>
            <EuiEmptyPrompt
              body={
                <div>
                  <EuiText className={classes.text}>
                    <div style={{ paddingLeft: "16px", textAlign: "center" }}>
                      <span>
                        Create rule to suppress alerts you don't need. Filter out known maintenance
                        windows, expected spikes or non-critical systems.
                      </span>
                    </div>
                  </EuiText>
                  <EuiSpacer size="m" />
                  <AddButton
                    text="Create Rule"
                    onClick={() => {
                      if (noData === 0) onClick();
                    }}
                  />
                  <EuiSpacer size="s" />
                  <Button
                    type="empty"
                    text="Learn More"
                    onClick={() => {
                      window.open(noDataUrl, "_blank");
                    }}
                  />
                </div>
              }
              icon={
                <div
                  style={{
                    borderRadius: "50%",
                    backgroundColor: "#1b8dcd22",
                    width: "50px",
                    height: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  <EuiIcon type="advancedSettingsApp" color="var(--cyan)" size="l" />
                </div>
              }
              className={classes.wrapper}
              hasBorder={false}
              style={{ height, padding: type === "card" ? 0 : "20px" }}
              title={
                <h2 className={classes.title}>
                  <div style={{ display: "inline-flex" }}>Reduce the Noise</div>
                </h2>
              }
              titleSize="s"
            />
          </>
        )}

        {!loading && location?.pathname === exceptions[2] && noData === 0 && (
          <>
            <EuiEmptyPrompt
              body={
                <div>
                  <EuiText className={classes.text}>
                    <div style={{ paddingLeft: "16px", textAlign: "center" }}>
                      <span>
                        Get alerts where you work. Connect Email, Slack, Teams, PagerDuty, or your
                        ticketing system to receive notifications in real time.
                      </span>
                    </div>
                  </EuiText>
                  <EuiSpacer size="m" />
                  <AddButton
                    text="Add Channel"
                    onClick={() => {
                      if (noData === 0) onClick();
                    }}
                  />
                  <EuiSpacer size="s" />
                  <Button
                    type="empty"
                    text="View Integrations"
                    onClick={() => {
                      navigate(REDIRECT_TO_INTEGRATIONS);
                    }}
                  />
                </div>
              }
              icon={
                <div
                  style={{
                    borderRadius: "50%",
                    backgroundColor: "#1b8dcd22",
                    width: "50px",
                    height: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  <EuiIcon type="bell" color="var(--cyan)" size="l" />
                </div>
              }
              className={classes.wrapper}
              hasBorder={false}
              style={{ height, padding: type === "card" ? 0 : "20px" }}
              title={
                <h2 className={classes.title}>
                  <div style={{ display: "inline-flex" }}>Stay in the Loop</div>
                </h2>
              }
              titleSize="s"
            />
          </>
        )}

        {!loading && location?.pathname === exceptions[3] && noData === 0 && (
          <>
            <EuiEmptyPrompt
              body={
                <div>
                  <EuiText className={classes.text}>
                    <div style={{ paddingLeft: "16px", textAlign: "center" }}>
                      <span>
                        Build custom queries to track metrics specific to your workload. Set
                        thresholds, define conditions, and get alerted when things change.
                      </span>
                    </div>
                  </EuiText>
                  <EuiSpacer size="m" />
                  <AddButton
                    text="Create Custom Query"
                    onClick={() => {
                      if (noData === 0) onClick();
                    }}
                  />
                  <EuiSpacer size="s" />
                  {/* <Button type="empty" text="View Examples" onClick={() => {}} /> */}
                </div>
              }
              icon={
                <div
                  style={{
                    borderRadius: "50%",
                    backgroundColor: "#1b8dcd22",
                    width: "50px",
                    height: "50px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}>
                  <EuiIcon type="visGoal" color="var(--cyan)" size="l" />
                </div>
              }
              className={classes.wrapper}
              hasBorder={false}
              style={{ height, padding: type === "card" ? 0 : "20px" }}
              title={
                <h2 className={classes.title}>
                  <div style={{ display: "inline-flex" }}>Monitor What Matters</div>
                </h2>
              }
              titleSize="s"
            />
          </>
        )}
      </>
    );
  }
);

export default NoData;
